import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { BrowserViewportPreset } from '@chatsdet/types';
import { API_URL } from '../config';
import { toApiError } from '../lib/api-error';
import type { WorkflowListItem } from './useWorkflows';

interface UseWorkflowMutationsOptions {
  currentOrgId: string | null;
  workflowId: string | null;
  onWorkflowCreated?: (workflow: any) => void;
  onWorkflowUpdated?: (workflow: any) => void;
  onWorkflowArchived?: (archivedId: string) => void;
  /** Promotion model (eligibility flag OFF): fired after a workflow is promoted. */
  onWorkflowPromoted?: (workflow: WorkflowListItem) => void;
}

export function useWorkflowMutations({
  currentOrgId,
  workflowId,
  onWorkflowCreated,
  onWorkflowUpdated,
  onWorkflowArchived,
  onWorkflowPromoted,
}: UseWorkflowMutationsOptions) {
  const qc = useQueryClient();
  const invalidateWorkflowLists = () => {
    qc.invalidateQueries({ queryKey: ['workflows'] });
    qc.invalidateQueries({ queryKey: ['workflows', currentOrgId] });
  };

  // Create workflow mutation
  const createWorkflowMutation = useMutation({
    mutationFn: async (input?: { name?: string; description?: string; status?: string; flowType?: string }) => {
      const r = await fetch(`${API_URL}/workflows`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          name: input?.name ?? 'Untitled Workflow',
          status: input?.status ?? 'draft',
          ...(input?.description ? { description: input.description } : {}),
          ...(input?.flowType ? { flowType: input.flowType } : {}),
        }),
      });
      if (!r.ok) throw await toApiError(r, 'Failed to create workflow');
      return r.json();
    },
    onSuccess: (data) => {
      invalidateWorkflowLists();
      onWorkflowCreated?.(data.workflow);
      if (data?.workflow?.id) {
        qc.invalidateQueries({ queryKey: ['workflow-detail', data.workflow.id] });
      }
    },
  });

  // Update workflow mutation
  const updateWorkflowMutation = useMutation({
    mutationFn: async (updates: {
      name?: string;
      description?: string | null;
      preflight?: string | null;
      cleanup?: string | null;
      status?: string;
      flowType?: string;
      runAgentically?: boolean;
      seedRunOncePerSuite?: boolean;
      environmentConstraints?:
        | { mode: 'only'; environmentIds: string[] }
        | { mode: 'envTypes'; types: Array<'permanent' | 'ephemeral' | 'local' | 'sandbox'> }
        | null;
      defaultEnvironmentId?: string | null;
      viewportSize?: BrowserViewportPreset | null;
    }) => {
      if (!workflowId) {
        throw new Error('No workflow ID');
      }

      const payload: Record<string, unknown> = {};
      if (updates.name !== undefined) payload.name = updates.name;
      if (updates.description !== undefined) payload.description = updates.description;
      if (updates.preflight !== undefined) payload.preflight = updates.preflight;
      if (updates.cleanup !== undefined) payload.cleanup = updates.cleanup;
      if (updates.status !== undefined) payload.status = updates.status;
      if (updates.flowType !== undefined) payload.flowType = updates.flowType;
      if (updates.runAgentically !== undefined) payload.runAgentically = updates.runAgentically;
      if (updates.seedRunOncePerSuite !== undefined) {
        payload.seedRunOncePerSuite = updates.seedRunOncePerSuite;
      }
      if (updates.environmentConstraints !== undefined) {
        payload.environmentConstraints = updates.environmentConstraints;
      }
      if (updates.defaultEnvironmentId !== undefined) {
        payload.defaultEnvironmentId = updates.defaultEnvironmentId;
      }
      if (updates.viewportSize !== undefined) payload.viewportSize = updates.viewportSize;

      if (Object.keys(payload).length === 0) {
        return null;
      }

      const r = await fetch(`${API_URL}/workflows/${workflowId}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!r.ok) throw await toApiError(r, 'Failed to update workflow');
      return r.json();
    },
    onSuccess: (data) => {
      if (data?.workflow) {
        onWorkflowUpdated?.(data.workflow);
      }
      invalidateWorkflowLists();
      if (workflowId) {
        qc.invalidateQueries({ queryKey: ['workflow-detail', workflowId] });
      }
    },
  });

  // Archive workflow mutation (soft-delete). Archived workflows are hidden from
  // lists and excluded from test suites; the row stays in the DB so historical
  // run/test-run permalinks keep resolving.
  //
  // Wire-format note: this hook accepts archive-vocabulary fields but the route
  // (`POST /workflows/delete`) still reads the legacy `deletedFrom`/`timeToDeleteMs`
  // keys for backward compatibility. The rename is intentional at this boundary.
  const archiveWorkflowMutation = useMutation({
    mutationFn: async (params: {
      id: string;
      archivedFrom?: string;
      confirmationMethod?: string;
      timeToArchiveMs?: number;
    }) => {
      const r = await fetch(`${API_URL}/workflows/delete`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          id: params.id,
          deletedFrom: params.archivedFrom,
          confirmationMethod: params.confirmationMethod,
          timeToDeleteMs: params.timeToArchiveMs,
        }),
      });
      if (!r.ok) throw await toApiError(r, 'Failed to archive workflow');
      return r.json();
    },
    onSuccess: async (_data, params) => {
      await Promise.all([
        qc.refetchQueries({ queryKey: ['workflows'] }),
        qc.refetchQueries({ queryKey: ['workflows', currentOrgId] }),
      ]);
      qc.invalidateQueries({ queryKey: ['workflow-detail', params.id] });
      onWorkflowArchived?.(params.id);
    },
  });

  const unarchiveWorkflowMutation = useMutation({
    mutationFn: async (id: string) => {
      const r = await fetch(`${API_URL}/workflows/${id}/unarchive`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!r.ok) throw await toApiError(r, 'Failed to restore workflow');
      return r.json();
    },
    onSuccess: async (_data, id) => {
      await Promise.all([
        qc.refetchQueries({ queryKey: ['workflows'] }),
        qc.refetchQueries({ queryKey: ['workflows', currentOrgId] }),
      ]);
      qc.invalidateQueries({ queryKey: ['workflow-detail', id] });
    },
  });

  // Create node mutation
  const createNodeMutation = useMutation({
    mutationKey: ['workflow-node-create'],
    mutationFn: async (params: {
      workflowId: string;
      node: {
        nodeType: string;
        configJson?: any;
        canonicalPageId?: string;
        canonicalActionId?: string;
        orderIndex: number;
      };
    }) => {
      const r = await fetch(`${API_URL}/workflows/${params.workflowId}/nodes`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(params.node),
      });
      if (!r.ok) throw await toApiError(r, 'Failed to create node');
      return r.json();
    },
  });

  // Update node mutation
  const updateNodeMutation = useMutation({
    mutationKey: ['workflow-node-update'],
    mutationFn: async ({ nodeId, data }: { nodeId: string; data: any }) => {
      if (!workflowId) throw new Error('No workflow ID');
      const r = await fetch(`${API_URL}/workflows/${workflowId}/nodes/${nodeId}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (!r.ok) throw await toApiError(r, 'Failed to update node');
      return r.json();
    },
  });

  // Delete node mutation
  const deleteNodeMutation = useMutation({
    mutationFn: async (nodeId: string) => {
      if (!workflowId) throw new Error('No workflow ID');
      const r = await fetch(`${API_URL}/workflows/${workflowId}/nodes/${nodeId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!r.ok) throw await toApiError(r, 'Failed to delete node');
      return r.json();
    },
  });

  // Create edge mutation
  const createEdgeMutation = useMutation({
    mutationFn: async (params: {
      workflowId: string;
      edge: { sourceNodeId: string; targetNodeId: string; edgeType?: string };
    }) => {
      const r = await fetch(`${API_URL}/workflows/${params.workflowId}/edges`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(params.edge),
      });
      if (!r.ok) throw await toApiError(r, 'Failed to create edge');
      return r.json();
    },
  });

  // Bulk delete nodes with server-side reconnection
  const bulkDeleteNodesMutation = useMutation({
    mutationFn: async (params: { nodeIds: string[]; reconnect?: boolean }) => {
      if (!workflowId) throw new Error('No workflow ID');
      const r = await fetch(`${API_URL}/workflows/${workflowId}/nodes/bulk-delete`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(params),
      });
      if (!r.ok) throw await toApiError(r, 'Failed to bulk delete nodes');
      return r.json();
    },
  });

  // Delete edge mutation
  const deleteEdgeMutation = useMutation({
    mutationFn: async (edgeId: string) => {
      if (!workflowId) throw new Error('No workflow ID');
      const r = await fetch(`${API_URL}/workflows/${workflowId}/edges/${edgeId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!r.ok) throw await toApiError(r, 'Failed to delete edge');
      return r.json();
    },
  });

  // Atomic insert-node-on-edge mutation. Replaces the legacy 4-step
  // DELETE→POST→POST→POST dance with a single transactional backend call.
  const insertNodeOnEdgeMutation = useMutation({
    mutationFn: async (params: {
      edgeId: string;
      node: {
        nodeType: string;
        configJson?: Record<string, unknown> | null;
        canonicalPageId?: string | null;
        canonicalActionId?: string | null;
        orderIndex: number;
      };
    }) => {
      if (!workflowId) throw new Error('No workflow ID');
      const r = await fetch(
        `${API_URL}/workflows/${workflowId}/edges/${params.edgeId}/insert-node`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify(params.node),
        }
      );
      if (!r.ok) throw await toApiError(r, 'Failed to insert node on edge');
      return r.json() as Promise<{
        node: { id: string; nodeType: string; orderIndex: number; configJson?: unknown };
        sourceEdge: {
          id: string;
          sourceNodeId: string;
          targetNodeId: string;
          edgeType: string;
        };
        targetEdge: {
          id: string;
          sourceNodeId: string;
          targetNodeId: string;
          edgeType: string;
        };
      }>;
    },
  });

  // Fork workflow mutation (creates an editable draft copy linked to its origin)
  const forkWorkflowMutation = useMutation({
    mutationFn: async (workflowIdToFork: string) => {
      const r = await fetch(`${API_URL}/workflows/${workflowIdToFork}/fork`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!r.ok) throw await toApiError(r, 'Failed to fork workflow');
      return r.json();
    },
    onSuccess: (data) => {
      invalidateWorkflowLists();
      onWorkflowCreated?.(data.workflow);
    },
  });

  // Sync full draft state (used by undo/redo to atomically replace all nodes/edges)
  const syncDraftStateMutation = useMutation({
    mutationFn: async (state: {
      nodes: Array<{
        id: string;
        nodeType: string;
        orderIndex: number;
        configJson: Record<string, unknown>;
        canonicalPageId?: string | null;
        canonicalActionId?: string | null;
      }>;
      edges: Array<{
        id: string;
        sourceNodeId: string;
        targetNodeId: string;
        edgeType?: string;
      }>;
    }) => {
      if (!workflowId) throw new Error('No workflow ID');
      const r = await fetch(`${API_URL}/workflows/${workflowId}/draft-state`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(state),
      });
      if (!r.ok) throw await toApiError(r, 'Failed to sync draft state');
      return r.json();
    },
  });

  const approveWorkflowMutation = useMutation({
    mutationFn: async () => {
      if (!workflowId) throw new Error('No workflow ID');
      const response = await fetch(`${API_URL}/workflows/${workflowId}/approve`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!response.ok) throw await toApiError(response, 'Failed to approve workflow');
      return response.json();
    },
    onSuccess: (data) => {
      if (data?.workflow) {
        onWorkflowUpdated?.(data.workflow);
      }
      if (workflowId) {
        qc.invalidateQueries({ queryKey: ['workflow-detail', workflowId] });
      }
      invalidateWorkflowLists();
    },
  });

  // Promotion model (eligibility flag OFF): explicit promote / force-promote action.
  const promoteWorkflowMutation = useMutation({
    mutationFn: async (options?: { force?: boolean }) => {
      if (!workflowId) throw new Error('No workflow ID');
      const { force } = options ?? {};
      const response = await fetch(`${API_URL}/workflows/${workflowId}/promote`, {
        method: 'POST',
        credentials: 'include',
        headers: force ? { 'content-type': 'application/json' } : undefined,
        body: force ? JSON.stringify({ force }) : undefined,
      });

      if (!response.ok) throw await toApiError(response, 'Failed to promote workflow');

      return response.json();
    },
    onSuccess: (data) => {
      if (data?.workflow) {
        onWorkflowPromoted?.(data.workflow);
        onWorkflowUpdated?.(data.workflow);
      }
      if (workflowId) {
        qc.invalidateQueries({ queryKey: ['workflow-detail', workflowId] });
      }
      invalidateWorkflowLists();
    },
  });

  const pauseWorkflowMutation = useMutation({
    mutationFn: async () => {
      if (!workflowId) throw new Error('No workflow ID');
      const response = await fetch(`${API_URL}/workflows/${workflowId}/pause`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!response.ok) throw await toApiError(response, 'Failed to pause workflow');
      return response.json();
    },
    onSuccess: (data) => {
      if (data?.workflow) {
        onWorkflowUpdated?.(data.workflow);
      }
      if (workflowId) {
        qc.invalidateQueries({ queryKey: ['workflow-detail', workflowId] });
      }
      invalidateWorkflowLists();
    },
  });

  const unpauseWorkflowMutation = useMutation({
    mutationFn: async () => {
      if (!workflowId) throw new Error('No workflow ID');
      const response = await fetch(`${API_URL}/workflows/${workflowId}/unpause`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!response.ok) throw await toApiError(response, 'Failed to unpause workflow');
      return response.json();
    },
    onSuccess: (data) => {
      if (data?.workflow) {
        onWorkflowUpdated?.(data.workflow);
      }
      if (workflowId) {
        qc.invalidateQueries({ queryKey: ['workflow-detail', workflowId] });
      }
      invalidateWorkflowLists();
    },
  });

  return {
    createWorkflowMutation,
    updateWorkflowMutation,
    archiveWorkflowMutation,
    unarchiveWorkflowMutation,
    forkWorkflowMutation,
    createNodeMutation,
    updateNodeMutation,
    deleteNodeMutation,
    bulkDeleteNodesMutation,
    createEdgeMutation,
    deleteEdgeMutation,
    insertNodeOnEdgeMutation,
    approveWorkflowMutation,
    promoteWorkflowMutation,
    pauseWorkflowMutation,
    unpauseWorkflowMutation,
    syncDraftStateMutation,
  };
}
